import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2, CreditCard } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { paymentsApi } from '../lib/api';
import { getPendingPurchase } from '../components/PaymentMethodModal';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const action = searchParams.get('action');
  const [status, setStatus] = useState('polling');
  const [txn, setTxn] = useState(null);
  const [cardInfo, setCardInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }

    if (action === 'save_card') {
      // Handle card setup completion — retry up to 3 times with delay
      const checkSetup = async (attempt = 0) => {
        try {
          const data = await paymentsApi.checkSetupStatus(sessionId);
          if (data.status === 'complete') {
            setCardInfo(data.card);
            const pending = getPendingPurchase();
            if (pending?.returnTo) {
              sessionStorage.setItem('reframes_pending_purchase', JSON.stringify({ ...pending, autoOpen: true, timestamp: Date.now() }));
              navigate(pending.returnTo);
              return;
            }
            setStatus('card_saved');
          } else if (attempt < 2) {
            setTimeout(() => checkSetup(attempt + 1), 2000);
          } else {
            setStatus('error');
          }
        } catch {
          if (attempt < 2) {
            setTimeout(() => checkSetup(attempt + 1), 2000);
          } else {
            setStatus('error');
          }
        }
      };
      checkSetup();
      return;
    }

    if (action === 'subscription') {
      // Handle subscription confirmation
      const confirmSub = async () => {
        try {
          const data = await paymentsApi.confirmSubscription(sessionId);
          if (data.status === 'active') {
            setStatus('subscription_active');
            setTxn(data);
          } else {
            setStatus('error');
          }
        } catch {
          setStatus('error');
        }
      };
      confirmSub();
      return;
    }

    // Regular payment polling
    let attempts = 0;
    const poll = async () => {
      try {
        const data = await paymentsApi.getStatus(sessionId);
        setTxn(data);
        if (data.payment_status === 'paid') { setStatus('success'); return; }
        if (data.status === 'expired') { setStatus('expired'); return; }
        attempts++;
        if (attempts < 8) setTimeout(poll, 2000);
        else setStatus('timeout');
      } catch { setStatus('error'); }
    };
    poll();
  }, [sessionId, action]);

  return (
    <div className="min-h-screen bg-[#030014]" data-testid="payment-success-page">
      <Navbar />
      <div className="pt-32 flex items-center justify-center px-4">
        <div className="admin-card p-8 max-w-md w-full text-center">
          {status === 'polling' && (
            <>
              <Loader2 className="w-12 h-12 text-[#8B5CF6] mx-auto mb-4 animate-spin" />
              <h1 className="text-xl font-bold mb-2">Processing...</h1>
              <p className="text-gray-400 text-sm">Please wait while we confirm.</p>
            </>
          )}

          {status === 'card_saved' && (
            <>
              <CreditCard className="w-12 h-12 text-[#00FF9D] mx-auto mb-4" />
              <h1 className="text-xl font-bold mb-2">Card Saved!</h1>
              {cardInfo && (
                <p className="text-gray-400 text-sm mb-6" data-testid="saved-card-info">
                  {cardInfo.brand} ending in {cardInfo.last4}
                </p>
              )}
              <button onClick={() => navigate('/profile')}
                className="px-6 py-2.5 bg-[#8B5CF6] hover:bg-[#7C3AED] rounded-xl text-white font-medium transition-colors"
                data-testid="back-to-profile">
                Go to Profile
              </button>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-12 h-12 text-[#00FF9D] mx-auto mb-4" />
              <h1 className="text-xl font-bold mb-2">Payment Successful!</h1>
              <p className="text-gray-400 text-sm mb-1">
                {txn?.amount?.toFixed(2)} {txn?.currency?.toUpperCase()} processed.
              </p>
              <p className="text-xs text-gray-500 mb-6">Transaction: {txn?.transaction_id}</p>
              <button onClick={() => navigate('/browse')}
                className="px-6 py-2.5 bg-[#8B5CF6] hover:bg-[#7C3AED] rounded-xl text-white font-medium transition-colors"
                data-testid="back-to-browse">
                Continue Browsing
              </button>
            </>
          )}

          {status === 'subscription_active' && (
            <>
              <CheckCircle className="w-12 h-12 text-[#00FF9D] mx-auto mb-4" />
              <h1 className="text-xl font-bold mb-2">Welcome to Premium!</h1>
              <p className="text-gray-400 text-sm mb-1">Your subscription is now active.</p>
              <p className="text-xs text-gray-500 mb-6">
                Enjoy ad-free browsing. Next billing: {txn?.current_period_end ? new Date(txn.current_period_end).toLocaleDateString('en-US') : '30 days'}
              </p>
              <button onClick={() => navigate('/browse')}
                className="px-6 py-2.5 bg-[#8B5CF6] hover:bg-[#7C3AED] rounded-xl text-white font-medium transition-colors"
                data-testid="back-to-browse-sub">
                Start Browsing
              </button>
            </>
          )}

          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <>
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h1 className="text-xl font-bold mb-2">
                {status === 'expired' ? 'Session Expired' : 'Something went wrong'}
              </h1>
              <p className="text-gray-400 text-sm mb-6">
                {status === 'expired'
                  ? 'Your session has expired. Please try again.'
                  : 'We couldn\'t confirm. Please try again or contact support.'}
              </p>
              <button onClick={() => navigate('/profile')}
                className="px-6 py-2.5 bg-white/10 hover:bg-white/15 rounded-xl text-white font-medium transition-colors"
                data-testid="back-error">
                Back
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
